import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Typography, Table, Select, Checkbox, Space, Empty, Segmented, Button, Popconfirm, Tag, message } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import api from '../api';
import { usePageLabel } from '../components/BackChip';
import { UserContext } from '../App';
import EmployeeHistoryDrawer from '../components/EmployeeHistoryDrawer';

// The jobs counted under each skill heading.
const COUNTED_JOBS = ['Agent', 'Support Agent'];

// Oversight > Attendance & Schedule > WFM Skills Assign: a team's people as
// rows and the team's active WFM skills as columns (WFM Skills Config), ticked for
// who has which -- like the team's skills sheet. View sees it; Edit and up
// tick and untick (saved at once). Teams follow the user's Visible Teams.
// Invalid Assignments: skills people hold that are not their current team's
// (moved teams, or the skill's teams changed), to remove.
// EmployeeJobs: Agent (as campaign_summary's _AGENT_JOB_ID).
const AGENT_JOB_ID = 2;

export default function WFMSkillsAssignPage() {
  const [view, setView]       = useState('grid');   // grid | offteam
  const [offTeam, setOffTeam] = useState({ rows: [], can_edit: false });
  const [offLoading, setOffLoading] = useState(false);
  const loadOffTeam = useCallback(() => {
    setOffLoading(true);
    api.get('/api/wfm-skills/off-team').then((r) => setOffTeam(r.data))
      .catch(() => setOffTeam({ rows: [], can_edit: false }))
      .finally(() => setOffLoading(false));
  }, []);
  useEffect(() => { loadOffTeam(); }, [loadOffTeam]);
  const removeOffTeam = async (r) => {
    try {
      await api.put('/api/wfm-skills/assignments', { user_id: r.UserID, skill_id: r.SkillID, has: false });
      message.success(`${r.SkillName} removed from ${r.DisplayName}.`);
      loadOffTeam();
    } catch (err) {
      message.error(err.response?.data?.error || 'Remove failed.');
    }
  };
  // Sched Hrs opens the Employee History entry it came from: a link with
  // Employee History access, editable with Employee History Edit.
  const currentUser = useContext(UserContext);
  const historyLevel = currentUser?.CanEditStaffHistory ?? 0;
  const [historyOf, setHistoryOf] = useState(null);   // the person whose entry is open
  const [teams, setTeams]     = useState([]);
  const [teamId, setTeamId]   = useState(null);
  const [skills, setSkills]   = useState([]);
  const [people, setPeople]   = useState([]);
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy]       = useState(null);   // 'userId|skillId' being saved
  // Optional: only the skills that have this campaign / shift department
  // (WFM Skills Config).
  const [campaignId, setCampaignId] = useState(null);
  const [myCampaigns, setMyCampaigns] = useState([]);   // Visible Campaigns; null = all (admin)
  const [dept, setDept]             = useState(null);
  const teamName = teams.find((t) => t.ID === teamId)?.Name;
  usePageLabel(teamName ? `WFM Skills Assign: ${teamName}` : null);

  useEffect(() => {
    api.get('/api/teams', { params: { my_teams: 1, no_client_users: 1 } }).then((r) => {
      setTeams(r.data || []);
      if ((r.data || []).length === 1) setTeamId(r.data[0].ID);
    }).catch(() => setTeams([]));
    api.get('/api/wfm-skills', { params: { active: 1 } }).then((r) => setSkills(r.data || [])).catch(() => setSkills([]));
  }, []);

  const load = useCallback(() => {
    if (!teamId) { setPeople([]); return; }
    setLoading(true);
    api.get('/api/wfm-skills/assignments', { params: { team_id: teamId } })
      .then((r) => {
        setPeople(r.data.people || []); setCanEdit(!!r.data.can_edit);
        setMyCampaigns(r.data.campaign_ids === undefined ? [] : r.data.campaign_ids);
      })
      .catch(() => { setPeople([]); message.error('Failed to load the team.'); })
      .finally(() => setLoading(false));
  }, [teamId]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (person, skill, has) => {
    const key = `${person.ID}|${skill.ID}`;
    setBusy(key);
    try {
      await api.put('/api/wfm-skills/assignments', { user_id: person.ID, skill_id: skill.ID, has });
      setPeople((cur) => cur.map((p) => (p.ID !== person.ID ? p : {
        ...p, SkillIDs: has ? [...p.SkillIDs, skill.ID] : p.SkillIDs.filter((x) => x !== skill.ID),
      })));
    } catch (err) {
      message.error(err.response?.data?.error || 'Save failed.');
    } finally {
      setBusy(null);
    }
  };

  // The Campaign filter: campaigns the skills have, limited to the user's
  // Visible Campaigns (none set = none; admin every one).
  // The team's skills (WFM Skills Config > Teams); the filters work on these.
  const teamSkills = skills.filter((s) => s.Teams.some((t) => t.ID === teamId));
  const campaigns = [...new Map(teamSkills.flatMap((s) => s.Campaigns).map((c) => [c.ID, c])).values()]
    .filter((c) => myCampaigns === null || myCampaigns.includes(c.ID))
    .sort((a, b) => a.Name.localeCompare(b.Name));
  const depts = [...new Set(teamSkills.flatMap((s) => s.ShiftDepartments))].sort((a, b) => a.localeCompare(b));
  const shown = teamSkills.filter((s) => (!campaignId || s.Campaigns.some((c) => c.ID === campaignId))
    && (!dept || s.ShiftDepartments.includes(dept)));

  const day = (v) => (v ? dayjs(v).format('MM/DD/YYYY') : '—');
  const spaced = (v) => (v ? v.replace(/([a-z])([A-Z])/g, '$1 $2') : '');

  // Export to Excel: whichever view is showing. The grid exports the team's
  // people (by name) with the skill columns the Campaign / Shift Department
  // filters leave, "Yes" where they hold the skill.
  const exportExcel = () => {
    const stamp = dayjs().format('YYYY-MM-DD');
    const xday = (v) => (v ? dayjs(v).format('MM/DD/YYYY') : '');
    let rows;
    let file;
    if (view === 'offteam') {
      rows = offTeam.rows.map((r) => ({
        Name: r.DisplayName,
        'Current Team': r.TeamName || '',
        Skill: r.SkillActive ? r.SkillName : `${r.SkillName} (inactive)`,
        "Skill's Teams": (r.SkillTeams || []).join(', '),
        'Given On': xday(r.AssignedAt),
        'Given By': r.AssignedByName || '',
      }));
      file = `WFM Skills Invalid Assignments ${stamp}.xlsx`;
    } else {
      exportTeamGrid(stamp, xday);
      return;
    }
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Invalid Assignments');
    XLSX.writeFile(wb, file);
  };

  // The team grid laid out like the team's skills sheet: everyone with their
  // skills, agents first (by name), then a "Leadership Support Group" row and
  // everyone else (by job, then name). On the right the agents' count and FTE
  // (Sched Hrs / 40, as formulas so they follow edits to the sheet). Notes:
  // the Employee History entry's.
  const exportTeamGrid = (stamp, xday) => {
    const byName = (a, b) => (a.DisplayName || '').localeCompare(b.DisplayName || '');
    const agents = people.filter((p) => p.JobID === AGENT_JOB_ID).sort(byName);
    const others = people.filter((p) => p.JobID !== AGENT_JOB_ID)
      .sort((a, b) => (a.JobName || '').localeCompare(b.JobName || '') || byName(a, b));

    const head = ['Name', 'Paycor Status', 'Job', ...shown.map((s) => s.Name),
      'FT/PT', 'Start Date', 'Grad Date', 'Sched Hrs', 'Notes'];
    const hrsCol = head.indexOf('Sched Hrs');
    const row = (p) => [
      p.DisplayName, spaced(p.PaycorStatus), p.JobName || '',
      ...shown.map((s) => (p.SkillIDs.includes(s.ID) ? 'x' : '')),
      // No grad date: a truly empty cell (null), which the staff count
      // formulas below read as "in training".
      p.EmploymentType || '', xday(p.StartDate), p.GraduationDate ? xday(p.GraduationDate) : null,
      p.ScheduledHours ?? '', p.HistoryNotes || '',
    ];
    const ws = XLSX.utils.aoa_to_sheet([
      head, ...agents.map(row),
      ...(others.length ? [[], ['Leadership Support Group'], ...others.map(row)] : []),
    ]);

    // Staff counts, one empty column to the right: agents with a grad date
    // (Agent Staff Count), agents without one yet -- in training (New
    // Hires-Training Agent Count) -- and the graduated agents by skill (Agent Skills).
    // The agents are rows 2..n+1 (1-based); no agents -> zero, not a bad range.
    const last = agents.length + 1;
    const col = (c) => XLSX.utils.encode_col(c);
    const range = (c) => `${col(c)}2:${col(c)}${last}`;
    const grad = range(head.indexOf('Grad Date'));
    const hrs = range(hrsCol);
    // conds: [range, criterion] pairs, all of which a row must meet.
    const counts = (...conds) => {
      if (!agents.length) return [0, 0];
      const c = conds.map(([r, k]) => `${r},"${k}"`).join(',');
      return [{ f: `COUNTIFS(${c})` }, { f: `SUMIFS(${hrs},${c})/40` }];
    };
    XLSX.utils.sheet_add_aoa(ws, [
      ['Agent Staff Count', 'Qty', 'FTE'], ['Agents', ...counts([grad, '<>'])],
      [],
      ['New Hires-Training Agent Count', 'Qty', 'FTE'], ['Agents', ...counts([grad, ''])],
      [],
      ['Agent Skills', 'Qty', 'FTE'],
      ...shown.map((s, i) => [s.Name, ...counts([range(head.indexOf('Job') + 1 + i), 'x'], [grad, '<>'])]),
    ], { origin: { r: 0, c: head.length + 1 } });

    ws['!cols'] = [
      ...head.map((h) => ({ wch: h === 'Name' ? 24 : h === 'Notes' ? 30 : Math.max(8, h.length + 2) })),
      { wch: 3 }, { wch: 30 }, { wch: 6 }, { wch: 6 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Team Grid');
    XLSX.writeFile(wb, `WFM Skills ${teamName || ''} ${stamp}.xlsx`.replace(/[\\/:*?"<>|]/g, '-'));
  };
  const canExport = view === 'offteam' ? offTeam.rows.length > 0 : people.length > 0 && shown.length > 0;

  const columns = [
    // Paycor status under the name when it is not Active (e.g. on leave),
    // with Paycor's run-together words spaced: LeaveWithPay -> Leave With Pay.
    { title: 'Name', dataIndex: 'DisplayName', key: 'DisplayName', width: 220, fixed: 'left',
      sorter: (a, b) => (a.DisplayName || '').localeCompare(b.DisplayName || ''),
      render: (v, r) => (r.PaycorStatus ? (
        <div>
          {v}
          <div><Typography.Text type="warning" style={{ fontSize: 12 }}>
            {spaced(r.PaycorStatus)}
          </Typography.Text></div>
        </div>
      ) : v) },
    // Job and FT/PT stay narrow; the skill columns (no width) take any
    // spare room.
    // Sorted by Job, then Name, to start with.
    { title: 'Job', dataIndex: 'JobName', key: 'JobName', width: 120, defaultSortOrder: 'ascend',
      sorter: (a, b) => (a.JobName || '').localeCompare(b.JobName || '')
        || (a.DisplayName || '').localeCompare(b.DisplayName || ''),
      render: (v) => v || '—' },
    { title: 'FT/PT', dataIndex: 'EmploymentType', key: 'EmploymentType', width: 70,
      sorter: (a, b) => (a.EmploymentType || '').localeCompare(b.EmploymentType || ''), render: (v) => v || '—' },
    // From Employee History: the latest entry on this team.
    { title: 'Start Date', dataIndex: 'StartDate', key: 'StartDate', width: 100,
      sorter: (a, b) => (a.StartDate || '').localeCompare(b.StartDate || ''), render: day },
    { title: 'Grad Date', dataIndex: 'GraduationDate', key: 'GraduationDate', width: 100,
      sorter: (a, b) => (a.GraduationDate || '').localeCompare(b.GraduationDate || ''), render: day },
    { title: 'Sched Hrs', dataIndex: 'ScheduledHours', key: 'ScheduledHours', width: 80, align: 'right',
      sorter: (a, b) => (a.ScheduledHours ?? -1) - (b.ScheduledHours ?? -1),
      render: (v, r) => {
        const hrs = historyLevel >= 1 && r.HistoryID
          ? <Typography.Link onClick={() => setHistoryOf(r)}>{v ?? '—'}</Typography.Link>
          : (v ?? '—');
        // The entry has an End Date although they are still on the team.
        return r.HistoryEndDate ? (
          <div>
            {hrs}
            <div><Typography.Text type="warning" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
              Ended {day(r.HistoryEndDate)}
            </Typography.Text></div>
          </div>
        ) : hrs;
      } },
    ...shown.map((s) => ({
      // The team's count, and under it the Agent and Support Agent counts, one
      // line each (a job with none left out).
      title: (() => {
        const holders = people.filter((p) => p.SkillIDs.includes(s.ID));
        const byJob = {};
        holders.filter((p) => COUNTED_JOBS.includes(p.JobName))
          .forEach((p) => { byJob[p.JobName] = (byJob[p.JobName] || 0) + 1; });
        return (
          <div>
            <div>{`${s.Name} (${holders.length})`}</div>
            {Object.keys(byJob).length > 0 && (
              <div style={{ fontSize: 11, fontWeight: 400, opacity: 0.75, lineHeight: 1.3 }}>
                {COUNTED_JOBS.filter((j) => byJob[j]).map((j) => <div key={j}>{`${j} ${byJob[j]}`}</div>)}
              </div>
            )}
          </div>
        );
      })(),
      key: `skill-${s.ID}`,
      align: 'center',
      sorter: (a, b) => Number(a.SkillIDs.includes(s.ID)) - Number(b.SkillIDs.includes(s.ID)),
      filters: [{ text: 'Has it', value: 'yes' }, { text: "Doesn't", value: 'no' }],
      onFilter: (v, p) => p.SkillIDs.includes(s.ID) === (v === 'yes'),
      render: (_, p) => (
        <Checkbox checked={p.SkillIDs.includes(s.ID)} disabled={!canEdit || busy === `${p.ID}|${s.ID}`}
          onChange={(e) => toggle(p, s, e.target.checked)} />
      ),
    })),
    // The Employee History entry's notes (the same entry as Sched Hrs), a
    // link to the same panel. Last, after the skills.
    { title: 'Notes', dataIndex: 'HistoryNotes', key: 'HistoryNotes', width: 200, ellipsis: { showTitle: true },
      sorter: (a, b) => (a.HistoryNotes || '').localeCompare(b.HistoryNotes || ''),
      render: (v, r) => (historyLevel >= 1 && r.HistoryID
        ? <Typography.Link onClick={() => setHistoryOf(r)}>{v || '—'}</Typography.Link>
        : (v || '—')) },
  ];

  return (
    <>
      <Space align="center" wrap style={{ marginBottom: 12 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>WFM Skills Assign</Typography.Title>
        <Segmented value={view} onChange={setView} options={[
          { value: 'grid', label: 'Team Grid' },
          { value: 'offteam', label: `Invalid Assignments (${offTeam.rows.length})` },
        ]} />
        {view === 'grid' && (<>
        <Select style={{ width: 260 }} placeholder="Team" showSearch optionFilterProp="label"
          value={teamId} onChange={setTeamId}
          options={teams.map((t) => ({ value: t.ID, label: t.Name }))} />
        <Select style={{ width: 220 }} placeholder="Campaign" allowClear showSearch optionFilterProp="label"
          value={campaignId} onChange={(v) => setCampaignId(v ?? null)}
          options={campaigns.map((c) => ({ value: c.ID, label: c.Name }))} />
        <Select style={{ width: 240 }} placeholder="Shift Department" allowClear showSearch optionFilterProp="label"
          value={dept} onChange={(v) => setDept(v ?? null)}
          options={depts.map((d) => ({ value: d, label: d }))} />
        </>)}
        <Button icon={<DownloadOutlined />} onClick={exportExcel} disabled={!canExport}>Export</Button>
      </Space>
      {view === 'offteam' ? (
        <Table
          rowKey={(r) => `${r.UserID}|${r.SkillID}`}
          dataSource={offTeam.rows}
          loading={offLoading}
          size="small"
          pagination={false}
          sticky
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: 'Nobody holds a skill outside their team.' }}
          columns={[
            { title: 'Name', dataIndex: 'DisplayName', key: 'DisplayName', width: 200,
              sorter: (a, b) => (a.DisplayName || '').localeCompare(b.DisplayName || '') },
            { title: 'Current Team', dataIndex: 'TeamName', key: 'TeamName', width: 160,
              sorter: (a, b) => (a.TeamName || '').localeCompare(b.TeamName || ''), render: (v) => v || '—' },
            { title: 'Skill', dataIndex: 'SkillName', key: 'SkillName', width: 160,
              sorter: (a, b) => (a.SkillName || '').localeCompare(b.SkillName || ''),
              render: (v, r) => (r.SkillActive ? v : `${v} (inactive)`) },
            { title: "Skill's Teams", dataIndex: 'SkillTeams', key: 'SkillTeams', width: 220,
              render: (v) => (v.length ? <Space size={[4, 4]} wrap>{v.map((t) => <Tag key={t}>{t}</Tag>)}</Space> : '—') },
            { title: 'Given', key: 'given', width: 200,
              render: (_, r) => [r.AssignedAt && dayjs(r.AssignedAt).format('MM/DD/YYYY'), r.AssignedByName].filter(Boolean).join(' by ') || '—' },
            ...(offTeam.can_edit ? [{ title: '', key: 'remove', width: 100, align: 'right',
              render: (_, r) => (
                <Popconfirm title={`Remove ${r.SkillName} from ${r.DisplayName}?`} okText="Remove"
                  okButtonProps={{ danger: true }} onConfirm={() => removeOffTeam(r)}>
                  <Button size="small" danger>Remove</Button>
                </Popconfirm>
              ) }] : []),
          ]}
        />
      ) : !teamId ? <Empty description="Pick a team." />
        : !teamSkills.length ? <Empty description="No active WFM skills for this team." />
        : !shown.length ? <Empty description="No WFM skills match the filters." />
        : (
          <Table
            rowKey="ID"
            dataSource={people}
            columns={columns}
            loading={loading}
            size="small"
            pagination={false}
            sticky
            scroll={{ x: 'max-content' }}
            locale={{ emptyText: 'Nobody on this team.' }}
          />
        )}
      <EmployeeHistoryDrawer
        open={!!historyOf}
        userId={historyOf?.ID}
        historyId={historyOf?.HistoryID}
        name={historyOf?.DisplayName}
        canEdit={historyLevel >= 2}
        onClose={() => setHistoryOf(null)}
        onSaved={load}
      />
    </>
  );
}
